import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
    if (data.requires_2fa) return data;
    setUser(data);
    return data;
  }, []);

  const verify2fa = useCallback(async (tfaToken, code) => {
    const { data } = await axios.post(`${API}/auth/verify-2fa`, { tfa_token: tfaToken, code }, { withCredentials: true });
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (email, password, name, role) => {
    const { data } = await axios.post(`${API}/auth/register`, { email, password, name, role }, { withCredentials: true });
    setUser(data);
    return data;
  }, []);

  const googleSession = useCallback(async (sessionId) => {
    const { data } = await axios.post(`${API}/auth/google/session`, { session_id: sessionId }, { withCredentials: true });
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (err) { console.error('Logout error:', err); }
    setUser(false);
  }, []);

  const refreshUser = useCallback(async () => {
    await checkAuth();
  }, [checkAuth]);

  const isSuperAdmin = user?.role === 'operator_admin';

  const contextValue = useMemo(() => ({
    user, loading, login, verify2fa, register, googleSession, logout, refreshUser, isSuperAdmin
  }), [user, loading, login, verify2fa, register, googleSession, logout, refreshUser, isSuperAdmin]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
